import { getLocalName, isEqual, isNotEqual } from 'vet-bones/utils';

import {
  ModelRule,
  ModelRuleType,
} from 'src/containers/ProjectDiagram/types/data';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import {
  LinkData,
  Model,
  ModelData,
  NodeData,
} from 'src/containers/ProjectDiagram/types/model';
import { setColorIdForIri } from 'src/containers/ProjectDiagram/utils/colorIds';
import {
  isModelConceptClass,
  isModelConceptHierarchy,
  isModelConceptHierarchyLabel,
  isModelConceptRelationship,
  isModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/utils/data';
import { addDomainToAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addDomainToAttributeModelData';
import { addModelsToAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addModelsToAttributeModelData';
import { addRangeToAttributeModelData } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addRangeToAttributeModelData';
import {
  createModelConceptClass,
  getModelConceptClassKey,
  getModelConceptClassValue,
} from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { createModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchy';
import { createModelConceptHierarchyLabel } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/createModelConceptHierarchyLabel';
import { addUnconnectedModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addUnconnectedModelConceptRelationship';
import { createModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationship';
import { createModelConceptRelationshipLabel } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { addProjectModelIdsToIri } from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { setPropertyType } from 'src/containers/ProjectDiagram/utils/propertyType/setPropertyType';
import {
  restoreDeletedClass,
  restoreDeletedHierarchy,
  restoreDeletedRelationship,
} from 'src/containers/ProjectDiagram/utils/restoreDeletedData';
import { addRule } from 'src/containers/ProjectDiagram/utils/rules/addRule';
import { updateRule } from 'src/containers/ProjectDiagram/utils/rules/updateRule';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { ProjectModel } from 'src/store/state/projects';

const mergeLabelsByIri = (
  prevLabels: Record<string, string>,
  labels: Record<string, string>,
  replace?: boolean
) => {
  const nextLabels = { ...prevLabels };
  Object.entries(labels).forEach(([iri, label]) => {
    const existingLabel = nextLabels[iri];
    if (replace || !existingLabel || existingLabel === getLocalName(iri)) {
      nextLabels[iri] = label;
    }
  });
  return nextLabels;
};

const createOrUpdateData = <Data extends NodeData | LinkData>(
  data: Data,
  dataArray: Data[],
  isType: (data: any) => data is Data,
  projectModelIds: string[],
  model: Model,
  onAddData: Model['addNodeData'] | Model['addLinkData']
) => {
  const existingData = dataArray.find(
    (existing) => isType(existing) && existing.key === data.key
  );
  if (!existingData) {
    onAddData.call(model, data);
  }

  addProjectModelIdsToIri(data.value, projectModelIds)(model);
};

const createLabelData = <Data extends NodeData>(
  data: Data,
  isType: (data: any) => data is Data,
  model: Model
) => {
  const existingData = model.nodeDataArray.find(
    (existing) => isType(existing) && existing.key === data.key
  );
  if (!existingData) {
    model.addNodeData(data);
  }
};

const doesModelHaveClass = (model: Model, iri: string) => {
  const key = getModelConceptClassKey(iri);
  return Boolean(
    model.nodeDataArray.find(
      (nodeData) => isModelConceptClass(nodeData) && nodeData.key === key
    )
  );
};

/**
 * Checks if an unconnected relationship already exists in the model.
 * Used to prevent duplicate dangling relationships during model refresh.
 */
const hasUnconnectedRelationship = (
  model: Model,
  value: string,
  fromIri?: string,
  toIri?: string
) =>
  model.linkDataArray.some((linkData) => {
    if (!isModelConceptRelationship(linkData) || linkData.value !== value) {
      return false;
    }

    const existingFromIri = linkData.from
      ? getModelConceptClassValue(linkData.from)
      : undefined;
    const existingToIri = linkData.to
      ? getModelConceptClassValue(linkData.to)
      : undefined;

    return existingFromIri === fromIri && existingToIri === toIri;
  });

const addMetadataToDiagramModel = (
  projectModel: ProjectModel,
  model: Model,
  replace?: boolean
) => {
  if (projectModel.colorIdsByIri) {
    setDataProperty<ModelData>(model)(
      model.modelData,
      'colorIdsByIri',
      replace
        ? {
            ...model.modelData.colorIdsByIri,
            ...projectModel.colorIdsByIri,
          }
        : {
            // only add new, don't overwrite
            ...projectModel.colorIdsByIri,
            ...model.modelData.colorIdsByIri,
          }
    );
  }

  if (projectModel.descriptionsByIri) {
    setDataProperty<ModelData>(model)(
      model.modelData,
      'descriptionsByIri',
      replace
        ? {
            ...model.modelData.descriptionsByIri,
            ...projectModel.descriptionsByIri,
          }
        : {
            // only add new, don't overwrite
            ...projectModel.descriptionsByIri,
            ...model.modelData.descriptionsByIri,
          }
    );
  }

  if (projectModel.geoDataByIri) {
    setDataProperty<ModelData>(model)(
      model.modelData,
      'geoDataByIri',
      replace
        ? {
            ...model.modelData.geoDataByIri,
            ...projectModel.geoDataByIri,
          }
        : {
            // only add new, don't overwrite
            ...projectModel.geoDataByIri,
            ...model.modelData.geoDataByIri,
          }
    );
  }

  if (projectModel.labelsByIri) {
    setDataProperty<ModelData>(model)(
      model.modelData,
      'labelsByIri',
      mergeLabelsByIri(
        model.modelData.labelsByIri,
        projectModel.labelsByIri,
        replace
      )
    );
  }
};

const addNodesToDiagramModel = (
  projectModelIds: string[],
  projectModel: ProjectModel,
  model: Model
) => {
  // Get the set of class IRIs currently in the diagram
  const existingClassIris = new Set(
    model.nodeDataArray
      .filter(isModelConceptClass)
      .map((nodeData) => nodeData.value)
  );

  // Check if this model was previously imported by seeing if it has any
  // IRIs already tracked in modelsByIri. This check is done BEFORE adding
  // any classes to avoid false positives during the current operation.
  const modelWasPreviouslyImported = Object.values(
    model.modelData.modelsByIri
  ).some((modelIds) => modelIds.includes(projectModel.id));

  Object.values(projectModel.classes || {}).forEach(({ iri }) => {
    const isClassInDiagram = existingClassIris.has(iri);
    const isClassTrackedInModelsByIri = Boolean(
      model.modelData.modelsByIri[iri]
    );

    // Skip classes that were explicitly deleted by the user.
    // A class was deleted if:
    // 1. It's not currently in the diagram
    // 2. It's not tracked in modelsByIri (deletion removes from modelsByIri)
    // 3. The model was previously imported (so this isn't a fresh import)
    if (
      !isClassInDiagram &&
      !isClassTrackedInModelsByIri &&
      modelWasPreviouslyImported
    ) {
      return;
    }

    const node = createModelConceptClass({
      value: iri,
    });
    createOrUpdateData(
      node,
      model.nodeDataArray,
      isModelConceptClass,
      projectModelIds,
      model,
      model.addNodeData
    );

    restoreDeletedClass(node)(model);

    // assign a color to the class if it is not already defined
    if (!model.modelData.colorIdsByIri[iri]) {
      setColorIdForIri(iri)(model);
    }
  });
};

const addPropertiesToDiagramModel = (
  projectModelIds: string[],
  projectModel: ProjectModel,
  model: Model,
  diagram: Diagram,
  replace?: boolean
) => {
  Object.values(projectModel.classes || {}).forEach(({ iri, parents }) => {
    if (!doesModelHaveClass(model, iri)) {
      return;
    }

    parents.forEach((parent) => {
      if (!doesModelHaveClass(model, parent)) {
        return;
      }

      const link = createModelConceptHierarchy({
        fromIri: iri,
        toIri: parent,
      });
      createOrUpdateData(
        link,
        model.linkDataArray,
        isModelConceptHierarchy,
        projectModelIds,
        model,
        model.addLinkData
      );

      const linkLabel = createModelConceptHierarchyLabel({
        fromIri: iri,
        toIri: parent,
      });
      createLabelData(linkLabel, isModelConceptHierarchyLabel, model);

      restoreDeletedHierarchy(link, linkLabel)(model);
    });
  });

  Object.values(projectModel.properties || {}).forEach(
    ({
      iri: value,
      domain: domains,
      range: ranges,
      isDatatype,
      propertyType,
    }) => {
      setPropertyType(value, propertyType)(model);

      if (isDatatype) {
        addModelsToAttributeModelData({
          value,
          projectModelIds,
        })(model);

        ranges.forEach((range) => {
          addRangeToAttributeModelData({
            value,
            range,
          })(model);
        });

        domains.forEach((domain) => {
          if (!doesModelHaveClass(model, domain)) {
            return;
          }
          addDomainToAttributeModelData({
            value,
            domain,
          })(model);
        });
        return;
      }

      if (domains.length && ranges.length) {
        domains.forEach((fromIri) => {
          if (!doesModelHaveClass(model, fromIri)) {
            return;
          }

          ranges.forEach((toIri) => {
            if (!doesModelHaveClass(model, toIri)) {
              return;
            }

            const link = createModelConceptRelationship({
              fromIri,
              value,
              toIri,
            });
            createOrUpdateData(
              link,
              model.linkDataArray,
              isModelConceptRelationship,
              projectModelIds,
              model,
              model.addLinkData
            );

            const linkLabel = createModelConceptRelationshipLabel({
              fromIri,
              value,
              toIri,
            });
            createLabelData(linkLabel, isModelConceptRelationshipLabel, model);

            restoreDeletedRelationship(link, linkLabel)(model);
          });
        });
      } else if (domains.length) {
        domains.forEach((fromIri) => {
          if (!doesModelHaveClass(model, fromIri)) {
            return;
          }
          if (!replace || !hasUnconnectedRelationship(model, value, fromIri)) {
            addUnconnectedModelConceptRelationship({
              value,
              fromIri,
            })(model, diagram);
          }
          addProjectModelIdsToIri(value, projectModelIds)(model);
        });
      } else if (ranges.length) {
        ranges.forEach((toIri) => {
          if (!doesModelHaveClass(model, toIri)) {
            return;
          }
          if (
            !replace ||
            !hasUnconnectedRelationship(model, value, undefined, toIri)
          ) {
            addUnconnectedModelConceptRelationship({
              value,
              toIri,
            })(model, diagram);
          }
          addProjectModelIdsToIri(value, projectModelIds)(model);
        });
      } else {
        if (!replace || !hasUnconnectedRelationship(model, value)) {
          addUnconnectedModelConceptRelationship({
            value,
          })(model, diagram);
        }
        addProjectModelIdsToIri(value, projectModelIds)(model);
      }
    }
  );
};

const addRulesToDiagramModel = (
  projectModelIds: string[],
  projectModel: ProjectModel,
  model: Model,
  diagram: Diagram
) => {
  const existingRules = diagram.model.modelData.rulesById;

  Object.values(projectModel.rules || {}).forEach((newRule) => {
    // gojs doesn't support keys starting with _ (bnodes), use the uuid instead
    const id = newRule.id.replace(/^_:/, '');
    const rule: ModelRule = { ...newRule, id, models: projectModelIds };
    const { name, ifClause, thenClause, text, type } = rule;

    // existing rule with the same id is only updated if it changed
    if (existingRules[id]) {
      if (isNotEqual(rule, existingRules[id])) {
        updateRule(rule)(model);
      }
      return;
    }

    const rulesWithMatchingName = Object.values(existingRules).filter(
      (rule) => rule.name === name
    );

    if (!rulesWithMatchingName.length) {
      addRule(rule)(model);
    } else {
      // check if the rule is the same as an existing rule
      const areRulesEqual = rulesWithMatchingName.some(
        (existingRule) =>
          (type === ModelRuleType.QUERY_BUILDER &&
            existingRule.type === ModelRuleType.QUERY_BUILDER &&
            isEqual(ifClause, existingRule.ifClause) &&
            isEqual(thenClause, existingRule.thenClause)) ||
          (type === ModelRuleType.TEXT &&
            existingRule.type === ModelRuleType.TEXT &&
            text === existingRule.text)
      );
      if (!areRulesEqual) {
        // check for existing rule with name + _N
        const rulesWithMatchingNameAndSuffix = Object.values(
          existingRules
        ).filter((existingRule) => existingRule.name.startsWith(`${name}_`));

        const suffixes = rulesWithMatchingNameAndSuffix.map((existingRule) => {
          // regex to get the number after the last _
          const lastNumMatch = existingRule.name.match(/(.*)_([0-9]+)$/);
          return lastNumMatch?.[2] ? parseInt(lastNumMatch[2]) : 0;
        });
        const highestSuffix = Math.max(...suffixes);
        addRule({ ...rule, name: `${name}_${highestSuffix + 1}` })(model);
      }
    }
  });
};

export const addProjectModelsToDiagramModel =
  (
    projectModels: ProjectModel[],
    projectModelIds: string[] | null,
    replace?: boolean
  ) =>
  (model: Model, diagram: Diagram) => {
    // first add metadata and classes
    projectModels.forEach((projectModel) => {
      addMetadataToDiagramModel(projectModel, model, replace);
      addNodesToDiagramModel(
        projectModelIds || [projectModel.id],
        projectModel,
        model
      );
    });

    // add links and attributes second as they depend on the existence of classes
    projectModels.forEach((projectModel) =>
      addPropertiesToDiagramModel(
        projectModelIds || [projectModel.id],
        projectModel,
        model,
        diagram,
        replace
      )
    );

    // add rules last as they depend on the existence of links and attributes
    projectModels.forEach((projectModel) =>
      addRulesToDiagramModel(
        projectModelIds || [projectModel.id],
        projectModel,
        model,
        diagram
      )
    );
  };
